// Singleton AudioContext for iOS compatibility
// iOS Safari requires a user gesture to create/resume AudioContext
let audioContext: AudioContext | null = null;

/**
 * Initialize the audio context on first user interaction.
 * Call this on any user gesture (button click, etc.) to ensure
 * audio notifications will work on iOS.
 */
export function initAudioContext(): void {
  if (!audioContext) {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        audioContext = new AudioContextClass();
        // Resume if suspended (iOS requirement)
        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(() => {});
        }
      }
    } catch (err) {
      console.log('AudioContext initialization failed:', err);
    }
  } else if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {});
  }
}

/**
 * Get the shared audio context, initializing if needed.
 * Returns null if audio is not available.
 */
function getAudioContext(): AudioContext | null {
  if (!audioContext) {
    initAudioContext();
  }
  return audioContext;
}

export function playNotification(type: 'siren' | 'beep' | 'alert' = 'siren') {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Resume context if suspended (iOS requirement)
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  const repeatCount = 3;
  const beepDuration = 1500;
  const pauseDuration = 500;

  for (let i = 0; i < repeatCount; i++) {
    const delay = i * (beepDuration + pauseDuration);

    setTimeout(() => {
      // Vibration notification (not supported on iOS)
      if ('vibrate' in navigator) {
        navigator.vibrate(beepDuration);
      }

      try {
        // Ensure context is resumed before creating audio nodes
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }

        const compressor = ctx.createDynamicsCompressor();
        compressor.threshold.setValueAtTime(-20, ctx.currentTime);
        compressor.knee.setValueAtTime(10, ctx.currentTime);
        compressor.ratio.setValueAtTime(4, ctx.currentTime);
        compressor.attack.setValueAtTime(0.005, ctx.currentTime);
        compressor.release.setValueAtTime(0.1, ctx.currentTime);
        compressor.connect(ctx.destination);

        if (type === 'siren') {
          playWhistle(compressor, ctx);
        } else if (type === 'beep') {
          playBeep(compressor, ctx);
        } else {
          playChime(compressor, ctx);
        }
      } catch (err) {
        console.log('Audio notification not available:', err);
      }
    }, delay);
  }
}

function playWhistle(dest: AudioNode, ctx: AudioContext) {
  const t = ctx.currentTime;
  const duration = 0.4;

  const baseFreq = 2800;
  const harmonics = [
    { ratio: 1, gain: 1.0 },
    { ratio: 2, gain: 0.4 },
    { ratio: 3, gain: 0.15 },
  ];

  harmonics.forEach(h => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(baseFreq * h.ratio, t);

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(h.gain * 0.3, t + 0.01);
    gain.gain.setValueAtTime(h.gain * 0.25, t + duration * 0.6);
    gain.gain.linearRampToValueAtTime(0, t + duration);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + duration);
  });

  const noise = ctx.createBufferSource();
  const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * 0.05;
  }
  noise.buffer = noiseBuffer;

  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.setValueAtTime(baseFreq, t);
  noiseFilter.Q.setValueAtTime(8, t);

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0, t);
  noiseGain.gain.linearRampToValueAtTime(0.15, t + 0.01);
  noiseGain.gain.setValueAtTime(0.12, t + duration * 0.6);
  noiseGain.gain.linearRampToValueAtTime(0, t + duration);

  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(dest);
  noise.start(t);
  noise.stop(t + duration);
}

function playBeep(dest: AudioNode, ctx: AudioContext) {
  const t = ctx.currentTime;
  const duration = 0.15;
  const gap = 0.1;
  const count = 5;

  for (let i = 0; i < count; i++) {
    const start = t + i * (duration + gap);
    const freq = 1200 + i * 100;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, start);

    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.5, start + 0.005);
    gain.gain.setValueAtTime(0.45, start + duration * 0.7);
    gain.gain.linearRampToValueAtTime(0, start + duration);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(start);
    osc.stop(start + duration);

    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freq * 2, start);
    gain2.gain.setValueAtTime(0, start);
    gain2.gain.linearRampToValueAtTime(0.15, start + 0.005);
    gain2.gain.linearRampToValueAtTime(0, start + duration);
    osc2.connect(gain2);
    gain2.connect(dest);
    osc2.start(start);
    osc2.stop(start + duration);
  }
}

function playChime(dest: AudioNode, ctx: AudioContext) {
  const t = ctx.currentTime;
  const notes = [
    { freq: 523.25, dur: 0.2 },
    { freq: 659.25, dur: 0.2 },
    { freq: 783.99, dur: 0.35 },
  ];

  let time = t;
  notes.forEach((note, idx) => {
    const isLast = idx === notes.length - 1;
    const vol = isLast ? 0.5 : 0.35;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(note.freq, time);
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(vol, time + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.001, time + note.dur);
    osc.connect(gain);
    gain.connect(dest);
    osc.start(time);
    osc.stop(time + note.dur);

    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(note.freq * 2, time);
    gain2.gain.setValueAtTime(0, time);
    gain2.gain.linearRampToValueAtTime(vol * 0.3, time + 0.008);
    gain2.gain.exponentialRampToValueAtTime(0.001, time + note.dur * 0.8);
    osc2.connect(gain2);
    gain2.connect(dest);
    osc2.start(time);
    osc2.stop(time + note.dur);

    time += note.dur;
  });
}
